
import { Component, Vue, Watch, Prop, Inject, Ref } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"
import { DateTime } from "luxon"

import { RecordUserAssignment, AssignmentOperation, AssignedHours } from "@/types/assignment"
import { Field } from "@/types/Field"
import { CaseDetails, CaseField, TimeZone } from "@/types"
import { TimeManagementSettings } from "@/types/timeManagement"
import { asUtc, convertToTimezone, stripTimeFromDateString } from "@/scripts/defaultTimezone"

import RecordAssignmentService from "@/services/recordAssignmentService"

import DateTimePicker from "@/components/Fields/DateTime.vue"

import apiAssignment from "@/services/api/api_assignment"
import { TenantFeatures } from "@/types/tenantFeatures"
import TimeDurationPicker from "./TimeDurationPicker.vue"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { UserSettings } from "@/types/userSettings"

const caseStore = namespace("cases")

interface DisplayRecordUserAssignment extends RecordUserAssignment {
	displayAssignedTime: number
}

@Component({
	components: {
		"date-time": DateTimePicker,
		"time-duration-picker": TimeDurationPicker
	}
})
export default class UserAssignments extends Vue {
	@Inject({ from: "recordAssignmentService", default: null }) recordAssignmentService!: RecordAssignmentService | null

	@Ref() hoursDurationPickers!: TimeDurationPicker[]

	// --- VueX ---

	@State timeManagementSettings!: TimeManagementSettings | null
	@State timeZones!: TimeZone[]
	@State displayTimezone!: TimeZone | null
	@caseStore.State fieldsAwaitingUpdate!: string[] | null

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getUserSettings") userSettings!: UserSettings

	@caseStore.Mutation
	setFieldAwaitingUpdate!: (fieldId: string) => void
	@caseStore.Mutation
	private removeFieldAwaitingUpdate!: (fieldId: string) => void

	// --- Props ---

	@Prop({ required: true })
	public currentCase!: CaseDetails

	@Prop({ required: true })
	userId!: string

	@Prop({ required: true })
	field!: Field

	@Prop({ type: Boolean, default: false })
	readonly!: boolean

	// --- Data ---

	focusedHours: string | null = null
	createDate: string | null = null
	createHours = 0
	createDateRemaining: number | string | null = null
	formattedCreateDateRemaining: string | null = null

	// --- Computed Properties ---

	/**
	 * Get the field on record marked as assignment date
	 * @returns {CaseField | null} Case field if assignment date field is on record, null if it is not set or not present
	 */
	get assignmentDateField (): CaseField | null {
		if (!this.recordAssignmentService || !this.field || !this.timeManagementSettings) return null
		return this.recordAssignmentService.getAssignmentDate(this.field, this.timeManagementSettings)
	}

	/**
	 * Get the current UI timezone
	 * @returns {string} IANA code for current timezone
	 */
	get ianaTimezone (): string {
		if (this.assignmentDateField && !this.assignmentDateField.valueTimezone) {
			const timezone = this.timeZones.find(tz => tz.id === this.assignmentDateField!.valueTimezone)
			if (timezone) {
				return timezone.iana
			}
		}

		return this.displayTimezone
			? this.displayTimezone.iana
			: Intl.DateTimeFormat().resolvedOptions().timeZone
	}

	/**
	 * Value of assignment date field
	 * @returns {string | null} ISO representation of assignment date. Null if not set or field is not on record.
	 */
	get assignmentDate (): string | null {
		if (!this.assignmentDateField || this.assignmentDateField.value === null || this.assignmentDateField.value === undefined) return null

		if (this.assignmentDateField.value instanceof Date) {
			return asUtc(this.assignmentDateField.value).toISOString()
		} else if (this.assignmentDateField.valueTimezone) {
			return asUtc(convertToTimezone(this.assignmentDateField.value, this.ianaTimezone)).toISOString()
		} else {
			return this.assignmentDateField.value
		}
	}

	/**
	 * Get the hours Field ID to be used for the user role Field on the current record.
	 * @returns {CaseField | null} Hours field. Null if not set or not added
	 */
	get hoursFieldId (): string | null {
		return this.recordAssignmentService
			?.getTimeManagementHoursForRecord(this.field, this.currentCase) ?? null
	}

	/**
	 * Get totals for this user role field's assigned hours
	 * @returns {AssignedHours | null} Assigned hours from record assignments. Null if hours field not added
	 */
	get assignedHours (): AssignedHours | null {
		if (!this.recordAssignmentService || !this.hoursFieldId) return null
		return this.recordAssignmentService?.hourFieldAssignments[this.hoursFieldId] ?? null
	}

	/**
	 * Get total hours for this record that are unassigned.
	 * @returns {number} Total unassigned time
	 */
	get unassignedTime (): number {
		return this.assignedHours?.unassignedTime ?? 0
	}

	/**
	 * Gets all assignments for this user on this field for this record
	 * @returns {RecordUserAssignment[]} Array of record assignments
	 */
	get assignments (): DisplayRecordUserAssignment[] {
		if (!this.recordAssignmentService) return []

		return this.recordAssignmentService.assignments.filter(a => a.userId === this.userId && a.fieldId === this.field.id!)
			.map(a => ({
				...a,
				displayAssignedTime: a.hours
			}))
	}

	/**
	 * Gets the date of the last assignment
	 * @returns {string | null} Date of last assignment. Null if none present.
	 */
	get lastAssignmentDate (): string | null {
		return this.assignments.lastOrNull()?.date ?? null
	}

	/**
	 * Gets the value of the ScheduleManagementDisplayAsTime feature
	 * @returns { boolean } Value of the ScheduleManagementDisplayAsTime feature. False if not enabled.
	 */
	get scheduleManagementDisplayAsTime (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementDisplayAsTime)
	}

	@Watch("assignments")
	onAssignmentsChanged () {
		// Ensure that assignment inputs get updated when assignments change. Workaround
		// for a reactivity issue on numeric inputs.
		if (this.hoursDurationPickers) {
			this.hoursDurationPickers.forEach(picker => picker.forceUpdate())
		}
	}

	// --- Event Handlers ---

	/**
	 * Adds an assignment for the next date
	 */
	nextDate () {
		if (this.lastAssignmentDate) {
			this.assignTime(this.userId, this.field.id!, this.lastAssignmentDate, this.unassignedTime, AssignmentOperation.NextDay)
		}
	}

	/**
	 * Fills out remainder of unassigned time for this user
	 */
	autoFill () {
		if (this.lastAssignmentDate) {
			this.assignTime(this.userId, this.field.id!, this.lastAssignmentDate, this.unassignedTime, AssignmentOperation.AutoFill)
		}
	}

	/**
	 * Starts manually adding an additional date
	 */
	async addDate () {
		if (!this.lastAssignmentDate && !this.assignmentDate) return

		this.createHours = Math.min(9, this.unassignedTime)
		this.createDate = stripTimeFromDateString(this.lastAssignmentDate ? this.addDays(this.lastAssignmentDate, 1) : this.assignmentDate!)
		const hoursFree = await this.getHoursForDate(this.createDate)

		if (hoursFree !== null) {
			this.createHours = Math.max(0, Math.min(this.createHours, hoursFree))
		}
	}

	/**
	 * Add an assignment for an additional date
	 */
	addNew () {
		if (this.createDate) {
			this.assignTime(this.userId, this.field.id!, this.createDate, Math.max(0, this.createHours), AssignmentOperation.Set)
			this.createDate = null
			this.createDateRemaining = null
			this.createHours = 0
		}
	}

	/**
	 * Cancels adding an additional date
	 */
	cancelAdd () {
		this.createDate = null
		this.createDateRemaining = null
		this.createHours = 0
	}

	/**
	 * Updates the hours on a given date
	 * @param {DisplayRecordUserAssignment} assignment - Assignment to update
	 */
	setHoursForDateAndUnfocus (assignment: DisplayRecordUserAssignment) {
		if (assignment.displayAssignedTime < 0) {
			assignment.displayAssignedTime = 0
		}

		if (assignment.displayAssignedTime !== assignment.hours) {
			this.assignTime(this.userId, this.field.id!, assignment.date, assignment.displayAssignedTime, AssignmentOperation.Set)
		}

		if (this.focusedHours === assignment.date) {
			this.focusedHours = null
		}
	}

	/**
	 * Remmove an assignment
	 * @param {string} date - Date value to remove
	 */
	removeDate (date: string) {
		if (this.recordAssignmentService) {
			this.recordAssignmentService.remove(this.userId, this.field.id!, date)
		}
	}

	/**
	 * Sets the value of create date by stripping time from input
	 * @param {string | null} date - Date to set
	 */
	setCreateDate (date: string | null) {
		this.createDate = date ? stripTimeFromDateString(date) : null
		this.getHoursForDate(this.createDate)
	}

	// --- Utils ---

	/**
	 * Fetch the remaining capacity for a date
	 * @param {string | null} date - date to fetch for
	 * @returns {Promise<number | null>} Promise for number of hours free on date, or null if date not set or fails to match active
	 */
	async getHoursForDate (date: string | null): Promise<number | null> {
		if (!date) return null

		this.createDateRemaining = null
		const requestDate = date // Keep the request so that if the createDate changes while we're making the request, then we don't set the wrong value
		const dateUserValue = await apiAssignment.userTimeForDate(this.userId, stripTimeFromDateString(requestDate))
		if (requestDate === this.createDate) {
			const hoursRemaining = dateUserValue ? dateUserValue.capacityRemaining : 0
			if (this.scheduleManagementDisplayAsTime) {
				this.formattedCreateDateRemaining = this.getHoursInHoursAndMinutes(hoursRemaining)
			}
			return this.createDateRemaining = hoursRemaining
		} else {
			return null
		}
	}

	/**
	 * Add number of days to an iso date string
	 * @param {string} date - ISO string of date to add to
	 * @param {number} days - number of days to add
	 * @returns {string} iso date string
	 */
	addDays (date: string, days: number): string {
		return DateTime.fromISO(date).plus({ days }).toISO()
	}

	getHoursInHoursAndMinutes (hours: number) {
		if (hours === 0) {
			return "0 hours"
		}

		let hoursInMilliseconds = Math.round(hours * 60 * 60) * 1000
		if (hours < 0) {
			hoursInMilliseconds = -1 * hoursInMilliseconds
		}
		return formatDateMixin.methods.getFormattedTimeSpanDisplay(hoursInMilliseconds)
	}

	async assignTime (userId: string, fieldId: string | undefined, date: string, hours: number, assignmentOperation: AssignmentOperation) {
		if (this.recordAssignmentService) {
			const isHighLatencyMode = this.getIsFeatureEnabled(TenantFeatures.UserRoleFieldHighLatencyUserEnhancements) &&
                this.userSettings.settings["highLatencyMode"] && this.field.id // For high latency improvements

			if (isHighLatencyMode) this.$emit("field-awaiting-update", this.field.id!)

			await this.recordAssignmentService.assign(userId, fieldId!, date, hours, assignmentOperation)
			if (isHighLatencyMode) this.removeFieldAwaitingUpdate(this.field.id!)
		}
	}
}
